import { useMemo, useState } from 'react';
import { Card, Button, Input, Space, Typography, Alert, Divider, Tag, Collapse, Upload, Segmented, message, Steps, Checkbox, Tabs, Empty } from 'antd';
import { CloudUploadOutlined, DatabaseOutlined, FileTextOutlined, ExperimentOutlined, InboxOutlined, AccountBookOutlined, AppstoreOutlined, ThunderboltOutlined, SearchOutlined, FilterOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { importApi, type BasMetadataImportResult, type BasDataImportResult, type BasSubsystemsImportResult, type BasCatalogsMetadataImportResult, type BasScannedItem } from '@api/importApi';

const { Title, Paragraph, Text } = Typography;

type PhaseResult =
  | { kind: 'meta'; data: BasMetadataImportResult }
  | { kind: 'data'; data: BasDataImportResult }
  | { kind: 'subsystems'; data: BasSubsystemsImportResult }
  | { kind: 'catalogs-meta'; data: BasCatalogsMetadataImportResult };

type InputMode = 'upload' | 'path';

// Спільний root до КонфВигрузки. Бекенд-фази отримують уже зібрані шляхи —
// /Documents, /Subsystems, /Catalogs тощо. Якщо потрібен інший шлях для
// конкретної фази — змінюй root, не дублюй input.
const ACQ_ROOT_DEFAULT = 'D:/RDP/Демо/КонфВигрузка';

function acqJoinPath(acqRoot: string, acqSub: string): string {
  const acqClean = acqRoot.replace(/[\\/]+$/, ''); // прибрати завершальний слеш
  return `${acqClean}/${acqSub}`;
}

export default function BasImportPage() {
  const [acqRootDir, setAcqRootDir] = useState<string>(ACQ_ROOT_DEFAULT);
  const [acqDataFile, setAcqDataFile] = useState<string>('d:/Project/ac-quantum/plat.xml');
  const [acqInputMode, setAcqInputMode] = useState<InputMode>('upload');
  const [acqUploadedFile, setAcqUploadedFile] = useState<UploadFile | null>(null);

  // Selection state — null означає «імпортувати все» (legacy default).
  // Після scan'у заповнюються повними списками; користувач uncheck'ає що не треба.
  const [acqScanResult, acqSetScanResult] = useState<{
    documents: BasScannedItem[];
    catalogs: BasScannedItem[];
    subsystems: BasScannedItem[];
  } | null>(null);
  const [acqSelectedDocs, acqSetSelectedDocs] = useState<Set<string> | null>(null);
  const [acqSelectedCats, acqSetSelectedCats] = useState<Set<string> | null>(null);
  const [acqSelectedSubs, acqSetSelectedSubs] = useState<Set<string> | null>(null);
  const [acqScanLoading, acqSetScanLoading] = useState(false);

  // Конвертуємо null/Set → string[] | undefined для передачі в API. Empty Set → пустий масив (нічого не імпортувати).
  const acqDocsFilter = useMemo(
    () => (acqSelectedDocs ? Array.from(acqSelectedDocs) : undefined),
    [acqSelectedDocs],
  );
  const acqCatsFilter = useMemo(
    () => (acqSelectedCats ? Array.from(acqSelectedCats) : undefined),
    [acqSelectedCats],
  );
  const acqSubsFilter = useMemo(
    () => (acqSelectedSubs ? Array.from(acqSelectedSubs) : undefined),
    [acqSelectedSubs],
  );

  const [acqLoading, setAcqLoading] = useState<string | null>(null);
  const [acqResults, setAcqResults] = useState<Record<string, PhaseResult | null>>({
    phase0: null,
    phase0b: null,
    phase0d: null,
    phase1: null,
    phase2: null,
    phase3: null,
  });

  const acqQc = useQueryClient();
  const acqStatusQuery = useQuery({
    queryKey: ['import-status'],
    queryFn: () => importApi.getBasStatus(),
    refetchOnWindowFocus: false,
    staleTime: 5_000,
  });
  const acqStatus = acqStatusQuery.data;
  // 8 — кількість seed-типів у meta_document_types (без BAS-імпорту). Більше = Phase 0 пройшов.
  const acqPhase0Done = (acqStatus?.metadataTypes ?? 0) > 8;
  const acqPhase0bDone = (acqStatus?.subsystems ?? 0) > 0;
  const acqPhase1Done = (acqStatus?.catalogs ?? 0) > 0;
  const acqPhase2Done = (acqStatus?.documents ?? 0) > 0;
  const acqPhase3Done = (acqStatus?.journalEntries ?? 0) > 0;
  // Інвалідовуємо всі query, на які впливає BAS-імпорт — щоб після фаз UI оновився без F5.
  const acqInvalidateStatus = () => {
    acqQc.invalidateQueries({ queryKey: ['import-status'] });
    acqQc.invalidateQueries({ queryKey: ['subsystems'] });            // верхнє меню (CategoryNav)
    acqQc.invalidateQueries({ queryKey: ['metadata', 'document-types'] }); // конструктор + меню
    acqQc.invalidateQueries({ queryKey: ['metadata', 'catalog-types'] });  // CatalogTypesPage / DynamicCatalogItemsPage
    acqQc.invalidateQueries({ queryKey: ['catalog-items'] });          // списки записів каталогів
    acqQc.invalidateQueries({ queryKey: ['metadata', 'dynamic-documents'] }); // списки документів
    acqQc.invalidateQueries({ queryKey: ['ref'] });                    // ReferencePicker label-resolve cache
  };

  const acqUploadProps: UploadProps = {
    accept: '.xml',
    multiple: false,
    beforeUpload: (file) => {
      setAcqUploadedFile(file as UploadFile);
      return false; // prevent auto-upload, we trigger manually
    },
    onRemove: () => {
      setAcqUploadedFile(null);
    },
    fileList: acqUploadedFile ? [acqUploadedFile] : [],
    maxCount: 1,
  };

  async function runPhase0(acqDry: boolean) {
    setAcqLoading('phase0');
    try {
      const acqR = await importApi.importBasMetadata(acqJoinPath(acqRootDir, 'Documents'), acqDry, acqDocsFilter);
      setAcqResults(prev => ({ ...prev, phase0: { kind: 'meta', data: acqR } }));
      message.success(
        `Phase 0 ${acqDry ? '(dry-run)' : ''}: ${acqR.typesCreated} типів, ${acqR.attributesCreated} атрибутів`,
      );
    } catch (e: unknown) {
      message.error(`Phase 0 error: ${(e as Error).message}`);
    } finally {
      setAcqLoading(null);
      acqInvalidateStatus();
    }
  }

  async function runPhase0b() {
    setAcqLoading('phase0b');
    try {
      const acqR = await importApi.importBasSubsystems(acqJoinPath(acqRootDir, 'Subsystems'), acqSubsFilter);
      setAcqResults(prev => ({ ...prev, phase0b: { kind: 'subsystems', data: acqR } }));
      message.success(`Phase 0b: ${acqR.subsystemsCreated} створено / ${acqR.subsystemsUpdated} оновлено / ${acqR.documentLinksCreated} зв'язків (${acqR.elapsedMs}ms)`);
    } catch (e: unknown) {
      message.error(`Phase 0b error: ${(e as Error).message}`);
    } finally {
      setAcqLoading(null);
      acqInvalidateStatus();
    }
  }

  async function runPhase0d(acqDry: boolean) {
    setAcqLoading('phase0d');
    try {
      const acqR = await importApi.importBasCatalogsMetadata(acqJoinPath(acqRootDir, 'Catalogs'), acqDry, acqCatsFilter);
      setAcqResults(prev => ({ ...prev, phase0d: { kind: 'catalogs-meta', data: acqR } }));
      message.success(`Phase 0d ${acqDry ? '(dry-run)' : ''}: ${acqR.typesCreated} типів, ${acqR.attributesCreated} атрибутів (${acqR.elapsedMs}ms)`);
    } catch (e: unknown) {
      message.error(`Phase 0d error: ${(e as Error).message}`);
    } finally {
      setAcqLoading(null);
      acqInvalidateStatus();
    }
  }

  async function runScan() {
    acqSetScanLoading(true);
    try {
      const acqR = await importApi.scanBasConfig(acqRootDir);
      acqSetScanResult({ documents: acqR.documents, catalogs: acqR.catalogs, subsystems: acqR.subsystems });
      // За замовчуванням — все вибрано (whitelist = всі знайдені).
      acqSetSelectedDocs(new Set(acqR.documents.map(d => d.basName)));
      acqSetSelectedCats(new Set(acqR.catalogs.map(c => c.basName)));
      acqSetSelectedSubs(new Set(acqR.subsystems.map(s => s.basName)));
      message.success(
        `Просканoвано: ${acqR.documents.length} документів · ${acqR.catalogs.length} довідників · ${acqR.subsystems.length} підсистем`,
      );
      if (acqR.warnings.length > 0) message.warning(acqR.warnings.join('; '));
    } catch (e: unknown) {
      message.error(`Scan error: ${(e as Error).message}`);
    } finally {
      acqSetScanLoading(false);
    }
  }

  async function runPhase1() {
    setAcqLoading('phase1');
    try {
      const acqR = acqInputMode === 'upload' && acqUploadedFile
        ? await importApi.importBasCatalogsUpload(acqUploadedFile as unknown as File)
        : await importApi.importBasCatalogs(acqDataFile);
      setAcqResults(prev => ({ ...prev, phase1: { kind: 'data', data: acqR } }));
      message.success(`Phase 1: ${acqR.recordsCreated} створено, ${acqR.recordsLinkedOnly} залінковано (${acqR.elapsedMs}ms)`);
    } catch (e: unknown) {
      message.error(`Phase 1 error: ${(e as Error).message}`);
    } finally {
      setAcqLoading(null);
      acqInvalidateStatus();
    }
  }

  async function runPhase2() {
    setAcqLoading('phase2');
    try {
      const acqR = acqInputMode === 'upload' && acqUploadedFile
        ? await importApi.importBasDocumentsUpload(acqUploadedFile as unknown as File)
        : await importApi.importBasDocuments(acqDataFile);
      setAcqResults(prev => ({ ...prev, phase2: { kind: 'data', data: acqR } }));
      message.success(`Phase 2: ${acqR.recordsCreated} документів (${acqR.elapsedMs}ms)`);
    } catch (e: unknown) {
      message.error(`Phase 2 error: ${(e as Error).message}`);
    } finally {
      setAcqLoading(null);
      acqInvalidateStatus();
    }
  }

  async function runPhase3() {
    setAcqLoading('phase3');
    try {
      const acqR = acqInputMode === 'upload' && acqUploadedFile
        ? await importApi.importBasPostingsUpload(acqUploadedFile as unknown as File)
        : await importApi.importBasPostings(acqDataFile);
      setAcqResults(prev => ({ ...prev, phase3: { kind: 'data', data: acqR } }));
      message.success(`Phase 3: ${acqR.recordsCreated} проводок (${acqR.elapsedMs}ms)`);
    } catch (e: unknown) {
      message.error(`Phase 3 error: ${(e as Error).message}`);
    } finally {
      setAcqLoading(null);
      acqInvalidateStatus();
    }
  }

  // ── «Запустити все» — послідовно Phase 0 → 0d → 0b → 1 → 2 → 3.
  // Якщо якась з фаз падає — зупиняємо ланцюжок (наступні залежать від попередніх).
  const [acqRunAllStep, acqSetRunAllStep] = useState<number>(-1);
  const [acqRunAllStatus, acqSetRunAllStatus] = useState<'wait' | 'process' | 'finish' | 'error'>('wait');

  async function runAll() {
    if (!acqDataSourceReady) {
      message.warning('Спочатку вкажи XML-файл вигрузки даних (upload або шлях) для Phase 1/2/3.');
      return;
    }
    acqSetRunAllStatus('process');
    acqSetRunAllStep(0);
    const acqSteps: Array<{ name: string; fn: () => Promise<void> }> = [
      { name: 'Phase 0 — типи документів', fn: () => runPhase0(false) },
      { name: 'Phase 0d — типи довідників', fn: () => runPhase0d(false) },
      { name: 'Phase 0b — підсистеми', fn: () => runPhase0b() },
      { name: 'Phase 1 — дані довідників', fn: () => runPhase1() },
      { name: 'Phase 2 — документи', fn: () => runPhase2() },
      { name: 'Phase 3 — проводки', fn: () => runPhase3() },
    ];
    for (let i = 0; i < acqSteps.length; i++) {
      acqSetRunAllStep(i);
      try {
        await acqSteps[i].fn();
      } catch (e: unknown) {
        acqSetRunAllStatus('error');
        message.error(`${acqSteps[i].name} впав: ${(e as Error).message}`);
        return;
      }
    }
    acqSetRunAllStep(acqSteps.length);
    acqSetRunAllStatus('finish');
    message.success('Всі фази виконано. Перевір каталоги і документи у меню.');
  }

  const acqDataSourceReady = acqInputMode === 'upload' ? !!acqUploadedFile : !!acqDataFile;
  const acqRunAllInProgress = acqLoading !== null && acqRunAllStatus === 'process';

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      <Title level={3}>Імпорт з BAS</Title>
      <Paragraph type="secondary">
        Послідовний імпорт BAS-конфігурації у систему. Кожен етап виконується ідемпотентно — повторний запуск пропускає вже імпортоване.
      </Paragraph>

      <Card style={{ marginBottom: 16 }}>
        <Space.Compact style={{ width: '100%' }}>
          <Input
            addonBefore="Шлях до КонфВигрузки"
            value={acqRootDir}
            onChange={e => setAcqRootDir(e.target.value)}
            placeholder={ACQ_ROOT_DEFAULT}
          />
          <Button
            icon={<SearchOutlined />}
            loading={acqScanLoading}
            onClick={runScan}
          >
            Просканувати
          </Button>
        </Space.Compact>
        <Paragraph type="secondary" style={{ margin: '8px 0 0 0', fontSize: 12 }}>
          Фази 0/0b беруть піддиректорії автоматично:{' '}
          <Text code>{acqJoinPath(acqRootDir, 'Documents')}</Text>,{' '}
          <Text code>{acqJoinPath(acqRootDir, 'Subsystems')}</Text>.
          Кнопка «Просканувати» показує що доступно для вибірного імпорту.
        </Paragraph>

        {acqScanResult && (
          <SelectionPanel
            acqScan={acqScanResult}
            acqSelectedDocs={acqSelectedDocs}
            acqSelectedCats={acqSelectedCats}
            acqSelectedSubs={acqSelectedSubs}
            acqOnDocsChange={acqSetSelectedDocs}
            acqOnCatsChange={acqSetSelectedCats}
            acqOnSubsChange={acqSetSelectedSubs}
          />
        )}

        <Divider style={{ margin: '16px 0 12px 0' }}>або одним кліком</Divider>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Paragraph type="secondary" style={{ margin: 0, fontSize: 12 }}>
            <b>Запустити все</b> = послідовно <Text code>Phase 0 → 0d → 0b → 1 → 2 → 3</Text>.{' '}
            Передумова: вказати шлях/upload XML-файлу вигрузки даних нижче (для Phase 1/2/3).
          </Paragraph>
          <Button
            type="primary"
            size="large"
            icon={<ThunderboltOutlined />}
            loading={acqRunAllInProgress}
            disabled={!acqDataSourceReady}
            onClick={runAll}
          >
            Запустити все (6 фаз)
          </Button>
          {acqRunAllStep >= 0 && (
            <Steps
              size="small"
              current={acqRunAllStep}
              status={acqRunAllStatus === 'error' ? 'error' : acqRunAllStatus === 'finish' ? 'finish' : 'process'}
              items={[
                { title: 'P0' }, { title: 'P0d' }, { title: 'P0b' },
                { title: 'P1' }, { title: 'P2' }, { title: 'P3' },
              ]}
            />
          )}
        </Space>
      </Card>

      <Card
        style={{ marginBottom: 16 }}
        title={
          <Space>
            <ExperimentOutlined />
            <span>Phase 0 — Структура типів документів</span>
          </Space>
        }
        extra={<Tag color="blue">meta_document_types + meta_attributes</Tag>}
      >
        <Paragraph type="secondary" style={{ marginBottom: 8 }}>
          Читає <Text code>{acqJoinPath(acqRootDir, 'Documents')}</Text> і створює MetaDocumentType +
          MetaAttribute для кожного знайденого типу.
        </Paragraph>
        <Space>
          <Button onClick={() => runPhase0(true)} loading={acqLoading === 'phase0'}>
            Dry-run (без запису)
          </Button>
          <Button
            type="primary"
            icon={<CloudUploadOutlined />}
            onClick={() => runPhase0(false)}
            loading={acqLoading === 'phase0'}
          >
            Імпортувати метадану
          </Button>
        </Space>
        {acqResults.phase0 && <Phase0Result data={(acqResults.phase0 as { data: BasMetadataImportResult }).data} />}
      </Card>

      <Card
        style={{ marginBottom: 16 }}
        title={
          <Space>
            <AppstoreOutlined />
            <span>Phase 0b — Підсистеми (меню BAS-стилю)</span>
          </Space>
        }
        extra={<Tag color="geekblue">subsystems + meta_document_type_subsystems</Tag>}
      >
        <Alert
          message={acqPhase0Done
            ? `Phase 0 виконано — ${acqStatus!.metadataTypes} типів${acqPhase0bDone ? ` · вже імпортовано ${acqStatus!.subsystems} підсистем` : ''}`
            : 'Потрібно виконати Phase 0 (метадата документів) спочатку'}
          type={acqPhase0Done ? 'success' : 'warning'}
          showIcon
          style={{ marginBottom: 12 }}
        />
        <Paragraph type="secondary" style={{ marginBottom: 8 }}>
          Парсить <Text code>{acqJoinPath(acqRootDir, 'Subsystems')}</Text>: рекурсивно
          читає <Text code>X.xml</Text> + <Text code>X/Subsystems/Y.xml</Text>, створює ієрархію підсистем
          (Банк і каса → Банк / Каса) та зв'язує документи з підсистемами через <Text code>Content/Item</Text>.
          Один документ може бути у кількох підсистемах.
        </Paragraph>
        <Button
          type="primary"
          icon={<CloudUploadOutlined />}
          onClick={runPhase0b}
          loading={acqLoading === 'phase0b'}
        >
          Імпортувати підсистеми
        </Button>
        {acqResults.phase0b && <Phase0bResult data={(acqResults.phase0b as { data: BasSubsystemsImportResult }).data} />}
      </Card>

      <Card
        style={{ marginBottom: 16 }}
        title={
          <Space>
            <DatabaseOutlined />
            <span>Phase 0d — Метадані довідників</span>
          </Space>
        }
        extra={<Tag color="cyan">meta_document_types (Kind=catalog) + meta_attributes</Tag>}
      >
        <Paragraph type="secondary" style={{ marginBottom: 8 }}>
          Парсить <Text code>{acqJoinPath(acqRootDir, 'Catalogs')}</Text>: для кожного <Text code>X.xml</Text>{' '}
          створює MetaCatalogType + MetaAttribute (стандартні Code/Name + custom-атрибути зі схеми BAS).
          Витягує IsHierarchical та OwnerType (для підпорядкованих, напр. одиниці виміру → номенклатура).
          Ідемпотентно — існуючі типи пропускає.
        </Paragraph>
        <Space>
          <Button onClick={() => runPhase0d(true)} loading={acqLoading === 'phase0d'}>
            Dry-run (без запису)
          </Button>
          <Button
            type="primary"
            icon={<CloudUploadOutlined />}
            onClick={() => runPhase0d(false)}
            loading={acqLoading === 'phase0d'}
          >
            Імпортувати метадані довідників
          </Button>
        </Space>
        {acqResults.phase0d && <Phase0dResult data={(acqResults.phase0d as { data: BasCatalogsMetadataImportResult }).data} />}
      </Card>

      <Divider>Phase 1 та 2 — з XML-файлу вигрузки даних (plat.xml)</Divider>

      <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
        <Segmented
          value={acqInputMode}
          onChange={(v) => setAcqInputMode(v as InputMode)}
          options={[
            { label: 'Завантажити файл', value: 'upload', icon: <InboxOutlined /> },
            { label: 'Шлях на сервері', value: 'path', icon: <FileTextOutlined /> },
          ]}
        />
        {acqInputMode === 'upload' ? (
          <Upload.Dragger {...acqUploadProps} style={{ padding: 8 }}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Натисніть або перетягніть XML-файл сюди</p>
            <p className="ant-upload-hint">Ліміт 200 MB. Файл буде завантажено на сервер, імпортовано і видалено.</p>
          </Upload.Dragger>
        ) : (
          <Space.Compact style={{ width: '100%' }}>
            <Input
              addonBefore="Шлях до XML на сервері"
              value={acqDataFile}
              onChange={e => setAcqDataFile(e.target.value)}
              placeholder="d:/Project/ac-quantum/plat.xml"
            />
          </Space.Compact>
        )}
      </Space>

      <Card
        style={{ marginBottom: 16 }}
        title={
          <Space>
            <DatabaseOutlined />
            <span>Phase 1 — Довідники</span>
          </Space>
        }
        extra={<Tag color="green">currencies, banks, counterparties, …</Tag>}
      >
        <Alert
          message={acqPhase0Done
            ? `Phase 0 виконано — ${acqStatus!.metadataTypes} типів${acqPhase1Done ? ` · вже імпортовано ${acqStatus!.catalogs} мапінгів довідників` : ''}`
            : 'Потрібно виконати Phase 0 (метадата документів) спочатку'}
          type={acqPhase0Done ? 'success' : 'warning'}
          showIcon
          style={{ marginBottom: 12 }}
        />
        <Paragraph type="secondary" style={{ marginBottom: 8 }}>
          Читає <Text code>CatalogObject.*</Text> записи з XML, створює або лінкує валюти, банки, контрагентів,
          договори, статті руху коштів тощо. Запис мапінгу BAS UUID → AcQuantum Id зберігається в{' '}
          <Text code>bas_import_map</Text> для Phase 2.
        </Paragraph>
        <Button
          type="primary"
          icon={<CloudUploadOutlined />}
          onClick={runPhase1}
          loading={acqLoading === 'phase1'}
          disabled={!acqDataSourceReady}
        >
          Імпортувати довідники
        </Button>
        {acqResults.phase1 && <PhaseDataResult data={(acqResults.phase1 as { data: BasDataImportResult }).data} />}
      </Card>

      <Card
        title={
          <Space>
            <FileTextOutlined />
            <span>Phase 2 — Документи</span>
          </Space>
        }
        extra={<Tag color="purple">documents + dynamic_documents + JSONB</Tag>}
      >
        <Alert
          message={(acqPhase0Done && acqPhase1Done)
            ? `Передумови виконано — Phase 0 (${acqStatus!.metadataTypes} типів) + Phase 1 (${acqStatus!.catalogs} мапінгів)`
            : `Потрібно: Phase 0 ${acqPhase0Done ? '✓' : '✗'} і Phase 1 ${acqPhase1Done ? '✓' : '✗'}`}
          type={(acqPhase0Done && acqPhase1Done) ? 'success' : 'warning'}
          showIcon
          style={{ marginBottom: 12 }}
        />
        <Paragraph type="secondary" style={{ marginBottom: 8 }}>
          Читає <Text code>DocumentObject.*</Text> записи, створює спільну шапку (documents), прив'язаний DynamicDocument
          та JSONB зі значеннями всіх реквізитів (включно з табличними частинами). Посилання на довідники резолвить
          через <Text code>bas_import_map</Text>.
        </Paragraph>
        <Button
          type="primary"
          icon={<CloudUploadOutlined />}
          onClick={runPhase2}
          loading={acqLoading === 'phase2'}
          disabled={!acqDataSourceReady}
        >
          Імпортувати документи
        </Button>
        {acqResults.phase2 && <PhaseDataResult data={(acqResults.phase2 as { data: BasDataImportResult }).data} />}
      </Card>

      <Card
        style={{ marginTop: 16 }}
        title={
          <Space>
            <AccountBookOutlined />
            <span>Phase 3 — Проводки</span>
          </Space>
        }
        extra={<Tag color="gold">journal_entries</Tag>}
      >
        <Alert
          message={(acqPhase1Done && acqPhase2Done)
            ? `Передумови виконано — Phase 1 (${acqStatus!.catalogs} мапінгів) + Phase 2 (${acqStatus!.documents} документів)${acqPhase3Done ? ` · вже імпортовано ${acqStatus!.journalEntries} проводок` : ''}`
            : `Потрібно: Phase 1 ${acqPhase1Done ? '✓' : '✗'} і Phase 2 ${acqPhase2Done ? '✓' : '✗'}`}
          type={(acqPhase1Done && acqPhase2Done) ? 'success' : 'warning'}
          showIcon
          style={{ marginBottom: 12 }}
        />
        <Paragraph type="secondary" style={{ marginBottom: 8 }}>
          Читає <Text code>AccountingRegisterRecordSet.Хозрасчетный</Text> (записи регістру бухобліку). Для кожного <Text code>Record</Text> створює <Text code>JournalEntry</Text>:
          резолвить рахунки Дт/Кт через <Text code>ChartOfAccountsObject</Text> з того ж файлу, субконто 1-3 (тип + значення) через <Text code>bas_import_map</Text>, документ-recorder через мапу Phase 2.
          Після цього ОСВ і Журнал проводок показують реальні суми.
        </Paragraph>
        <Button
          type="primary"
          icon={<CloudUploadOutlined />}
          onClick={runPhase3}
          loading={acqLoading === 'phase3'}
          disabled={!acqDataSourceReady}
        >
          Імпортувати проводки
        </Button>
        {acqResults.phase3 && <PhaseDataResult data={(acqResults.phase3 as { data: BasDataImportResult }).data} />}
      </Card>
    </div>
  );
}

function Phase0dResult({ data }: { data: BasCatalogsMetadataImportResult }) {
  return (
    <div style={{ marginTop: 12 }}>
      <Space wrap>
        <Tag color="blue">Файлів: {data.filesScanned}</Tag>
        <Tag color="green">Типів створено: {data.typesCreated}</Tag>
        <Tag>Пропущено: {data.typesSkipped}</Tag>
        <Tag color="cyan">Атрибутів: {data.attributesCreated}</Tag>
        <Tag>{data.elapsedMs} мс</Tag>
        {data.errors.length > 0 && <Tag color="red">Помилок: {data.errors.length}</Tag>}
      </Space>
      {data.errors.length > 0 && (
        <Alert
          type="error"
          style={{ marginTop: 8 }}
          message="Помилки"
          description={data.errors.slice(0, 5).join('; ')}
        />
      )}
    </div>
  );
}

function Phase0bResult({ data }: { data: BasSubsystemsImportResult }) {
  return (
    <div style={{ marginTop: 12 }}>
      <Space wrap>
        <Tag color="blue">Файлів: {data.filesScanned}</Tag>
        <Tag color="green">Створено: {data.subsystemsCreated}</Tag>
        <Tag color="cyan">Оновлено: {data.subsystemsUpdated}</Tag>
        <Tag color="purple">Зв'язків з документами: {data.documentLinksCreated}</Tag>
        {data.unresolvedDocuments > 0 && <Tag color="orange">Без типу: {data.unresolvedDocuments}</Tag>}
        <Tag>{data.elapsedMs} мс</Tag>
        {data.errors.length > 0 && <Tag color="red">Помилок: {data.errors.length}</Tag>}
      </Space>
      {data.errors.length > 0 && (
        <Alert
          type="error"
          style={{ marginTop: 8 }}
          message="Помилки"
          description={data.errors.slice(0, 5).join('; ')}
        />
      )}
    </div>
  );
}

function Phase0Result({ data }: { data: BasMetadataImportResult }) {
  return (
    <div style={{ marginTop: 12 }}>
      <Space wrap>
        <Tag color="blue">Файлів: {data.filesScanned}</Tag>
        <Tag color="green">Типів створено: {data.typesCreated}</Tag>
        <Tag>Пропущено: {data.typesSkipped}</Tag>
        <Tag color="cyan">Атрибутів: {data.attributesCreated}</Tag>
        {data.errorsByFile.length > 0 && <Tag color="red">Помилок: {data.errorsByFile.length}</Tag>}
      </Space>
      {data.warnings.length > 0 && (
        <Collapse
          ghost
          style={{ marginTop: 8 }}
          items={[
            {
              key: 'w',
              label: `Warnings (${data.warnings.length})`,
              children: data.warnings.slice(0, 30).map((w, i) => (
                <div key={i} style={{ fontSize: 12, color: '#888' }}>
                  {w}
                </div>
              )),
            },
          ]}
        />
      )}
      {data.errorsByFile.length > 0 && (
        <Alert
          type="error"
          style={{ marginTop: 8 }}
          message="Файли з помилками"
          description={data.errorsByFile.slice(0, 5).join('; ')}
        />
      )}
    </div>
  );
}

function PhaseDataResult({ data }: { data: BasDataImportResult }) {
  return (
    <div style={{ marginTop: 12 }}>
      <Space wrap>
        <Tag color="blue">Зчитано: {data.recordsScanned}</Tag>
        <Tag color="green">Створено: {data.recordsCreated}</Tag>
        <Tag color="cyan">Залінковано: {data.recordsLinkedOnly}</Tag>
        <Tag>Пропущено: {data.recordsSkipped}</Tag>
        <Tag>{data.elapsedMs} мс</Tag>
        {data.errors.length > 0 && <Tag color="red">Помилок: {data.errors.length}</Tag>}
      </Space>
      {Object.keys(data.createdByType).length > 0 && (
        <Collapse
          ghost
          style={{ marginTop: 8 }}
          items={[
            {
              key: 'c',
              label: `Створено по типах (${Object.keys(data.createdByType).length})`,
              children: Object.entries(data.createdByType)
                .sort((a, b) => b[1] - a[1])
                .map(([k, v]) => (
                  <div key={k}>
                    <Tag>{k}</Tag> <Text>{v}</Text>
                  </div>
                )),
            },
          ]}
        />
      )}
      {data.unsupportedTypes.length > 0 && (
        <Collapse
          ghost
          style={{ marginTop: 4 }}
          items={[
            {
              key: 'u',
              label: `Непідтримувані типи (${data.unsupportedTypes.length})`,
              children: data.unsupportedTypes.map((t, i) => (
                <Tag key={i}>{t}</Tag>
              )),
            },
          ]}
        />
      )}
      {data.errors.length > 0 && (
        <Alert
          type="error"
          style={{ marginTop: 8 }}
          message="Помилки"
          description={data.errors.slice(0, 5).map((e, i) => (
            <div key={i} style={{ fontSize: 12 }}>
              {e}
            </div>
          ))}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// SelectionPanel — UI «Що імпортувати»
// ────────────────────────────────────────────────────────────────────

/**
 * 3-табова панель для вибору що імпортувати (Документи / Довідники / Підсистеми).
 * Selected = Set | null. null означає «імпортувати все» (legacy default), Set — whitelist.
 * Empty Set означає «не імпортувати нічого з цього типу».
 */
function SelectionPanel({
  acqScan,
  acqSelectedDocs, acqSelectedCats, acqSelectedSubs,
  acqOnDocsChange, acqOnCatsChange, acqOnSubsChange,
}: {
  acqScan: { documents: BasScannedItem[]; catalogs: BasScannedItem[]; subsystems: BasScannedItem[] };
  acqSelectedDocs: Set<string> | null;
  acqSelectedCats: Set<string> | null;
  acqSelectedSubs: Set<string> | null;
  acqOnDocsChange: (s: Set<string> | null) => void;
  acqOnCatsChange: (s: Set<string> | null) => void;
  acqOnSubsChange: (s: Set<string> | null) => void;
}) {
  return (
    <Card
      size="small"
      style={{ marginTop: 12, background: '#fafafa', border: '1px solid #e5e5e5' }}
      title={<Space><FilterOutlined /> <Text strong>Що імпортувати</Text></Space>}
      extra={
        <Text type="secondary" style={{ fontSize: 12 }}>
          Whitelist передається у Phase 0/0d/0b. Phase 1/2 автоматично пропустять типи без MetaCatalogType/MetaDocumentType.
        </Text>
      }
    >
      <Tabs
        size="small"
        items={[
          {
            key: 'docs',
            label: <span>Документи <Tag>{acqSelectedDocs?.size ?? acqScan.documents.length} / {acqScan.documents.length}</Tag></span>,
            children: <CheckboxList
              acqItems={acqScan.documents}
              acqSelected={acqSelectedDocs}
              acqOnChange={acqOnDocsChange}
            />,
          },
          {
            key: 'cats',
            label: <span>Довідники <Tag>{acqSelectedCats?.size ?? acqScan.catalogs.length} / {acqScan.catalogs.length}</Tag></span>,
            children: <CheckboxList
              acqItems={acqScan.catalogs}
              acqSelected={acqSelectedCats}
              acqOnChange={acqOnCatsChange}
            />,
          },
          {
            key: 'subs',
            label: <span>Підсистеми <Tag>{acqSelectedSubs?.size ?? acqScan.subsystems.length} / {acqScan.subsystems.length}</Tag></span>,
            children: <CheckboxList
              acqItems={acqScan.subsystems}
              acqSelected={acqSelectedSubs}
              acqOnChange={acqOnSubsChange}
            />,
          },
        ]}
      />
    </Card>
  );
}

/** Список з пошуком + select-all/none + per-item checkbox. */
function CheckboxList({
  acqItems, acqSelected, acqOnChange,
}: {
  acqItems: BasScannedItem[];
  acqSelected: Set<string> | null;
  acqOnChange: (s: Set<string> | null) => void;
}) {
  const [acqQuery, acqSetQuery] = useState('');
  const acqFiltered = useMemo(() => {
    if (!acqQuery.trim()) return acqItems;
    const acqQ = acqQuery.trim().toLowerCase();
    return acqItems.filter(i =>
      i.uaName.toLowerCase().includes(acqQ) ||
      i.basName.toLowerCase().includes(acqQ) ||
      i.code.toLowerCase().includes(acqQ),
    );
  }, [acqItems, acqQuery]);

  const acqAllChecked = acqSelected != null && acqItems.every(i => acqSelected.has(i.basName));
  const acqAnyChecked = acqSelected != null && acqSelected.size > 0;

  if (acqItems.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нічого не знайдено у директорії" />;
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Пошук по назві / BAS-імені"
          allowClear
          value={acqQuery}
          onChange={(e) => acqSetQuery(e.target.value)}
          style={{ width: 320 }}
          size="small"
        />
        <Space size={4}>
          <Checkbox
            checked={acqAllChecked}
            indeterminate={acqAnyChecked && !acqAllChecked}
            onChange={(e) => {
              if (e.target.checked) acqOnChange(new Set(acqItems.map(i => i.basName)));
              else acqOnChange(new Set());
            }}
          >
            Усі
          </Checkbox>
          <Button size="small" type="link" onClick={() => acqOnChange(new Set())}>Зняти всі</Button>
        </Space>
      </Space>
      <div style={{ maxHeight: 280, overflowY: 'auto', background: '#fff', padding: 8, border: '1px solid #f0f0f0', borderRadius: 4 }}>
        {acqFiltered.map(i => {
          const acqChecked = acqSelected?.has(i.basName) ?? false;
          return (
            <div key={i.basName} style={{ padding: '2px 4px' }}>
              <Checkbox
                checked={acqChecked}
                onChange={(e) => {
                  const acqNext = new Set(acqSelected ?? []);
                  if (e.target.checked) acqNext.add(i.basName);
                  else acqNext.delete(i.basName);
                  acqOnChange(acqNext);
                }}
              >
                <Space size={6}>
                  <Text>{i.uaName}</Text>
                  <Text type="secondary" code style={{ fontSize: 11 }}>{i.basName}</Text>
                  <Text type="secondary" style={{ fontSize: 11 }}>· {i.code}</Text>
                </Space>
              </Checkbox>
            </div>
          );
        })}
        {acqFiltered.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="За пошуком нічого не знайдено" />}
      </div>
    </Space>
  );
}
